import type { IQuestion } from '@repo/types/questions/Question.interface';
import type {
  AnswerQuestionSingle,
  AnswerUserQuestionSingle,
  ExportAnswerSingle,
  FeedbackAnswerQuestionSingle,
  QuestionSingleContent,
  QuestionSingleData,
  QuestionSingleSerialized,
} from '@repo/types/questions/QuestionSingle';
import type { CorrectType } from '@repo/types/quiz';

import { QuestionType } from '@repo/types/enums/questionType';

export class QuestionSingle implements IQuestion {
  readonly type: QuestionType;
  readonly content: string;
  private readonly points: number = 0;
  private answers: AnswerQuestionSingle[];
  private selectedAnswer: AnswerUserQuestionSingle | null = null;
  private marked: FeedbackAnswerQuestionSingle | null = null;
  constructor(data: QuestionSingleData, selectedAnswer: AnswerUserQuestionSingle | null, marked: FeedbackAnswerQuestionSingle | null) {
    this.type = QuestionType.SINGLE;
    this.content = data.content;
    this.points = data.points;
    this.answers = data.answers;
    this.selectedAnswer = selectedAnswer;
    this.marked = marked;
  }

  getContent(): QuestionSingleContent {
    return {
      content: this.content,
      answers: this.answers,
    };
  }

  getMaxPoints(): number {
    return this.points;
  }

  getPoints(): number {
    if (this.marked == null) return 0;

    return this.marked.points ?? 0;
  }

  setAnswer(id: string): void {
    const answer = this.answers.find((a) => a.id == id);

    if (!answer) return;

    this.selectedAnswer = answer;
  }

  getAnswer(): AnswerUserQuestionSingle | null {
    return this.selectedAnswer;
  }

  isAnswered(): boolean {
    return this.selectedAnswer ? this.selectedAnswer.id != null : false;
  }

  resetAnswer(): void {
    this.selectedAnswer = null;
  }

  setMarked(marked: FeedbackAnswerQuestionSingle): void {
    this.marked = marked;
  }

  getMarked(): FeedbackAnswerQuestionSingle | null {
    return this.marked;
  }

  isMarked(): boolean {
    return !(this.marked == null);
  }

  correctState(): CorrectType {
    if (!this.isAnswered()) return null;

    const isCorrect: boolean | undefined = this.marked?.correct;
    if (isCorrect !== undefined) {
      return isCorrect;
    }

    const points: number = this.getPoints();
    const maxPoints: number = this.getMaxPoints();

    if (points === 0) return false;
    return points === maxPoints ? true : 'partially';
  }

  toJSON(): QuestionSingleSerialized {
    return {
      marked: this.marked,
      selectedAnswer: this.selectedAnswer,
    };
  }

  importAnswer(answer: ExportAnswerSingle): void {
    if (answer.id) {
      this.selectedAnswer = { id: answer.id };
    } else {
      this.selectedAnswer = { id: null };
    }
  }

  exportAnswer(): ExportAnswerSingle {
    return {
      id: this.selectedAnswer?.id || null,
    };
  }
}
